import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CircularProgress } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useProcessingStatus } from '../shared/hooks/useProcessingStatus';
import { ProcessingStatus } from '../shared/ProcessingStatus';
import { FileTable } from '../shared/FileTable';
import { ActionButtons } from '../shared/ActionButtons';
import { useSummarizeResumeApi } from './SummarizeResumeApi';
import { WaitingStates, FinishStates } from '../../FermaHelper';
import { get } from '../../../components/hooks/apiFetch';
import { useNotification } from '../../../components/Notification/NotificationContext';
import { useMountedRef } from '../../../components/hooks/useMountedRef';
import { AI_UTILITIES } from '../../../constants/aiUtilities';

export const SummarizeResumeViewer = ({ location }) => {
    const { inputIdentifier } = useParams();

    const initialFileNamesFromProps = location.state?.fileNames;
    const initialIdFromProps = location.state?.id;
    const initialCommentFromProps = location.state?.comment;

    const {
        // Access
        hasAccess,
        isCheckingAccess,
        accessError,

        // Id
        id,
        isLoadingId,
        idError,

        // File names
        fileNames,
        isLoadingFileNames,
        fileNamesError,

        // Comment
        comment,
        isLoadingComment,
        commentError,

        // Status
        data: status,
        error: statusError,
        isProcessing,
        fetchStatus,
        timerElapsedTime
    } = useProcessingStatus(`summarizeresume`, inputIdentifier, initialFileNamesFromProps, initialIdFromProps, initialCommentFromProps);

    const isWaiting = WaitingStates.includes(status?.status);
    const isFinished = FinishStates.includes(status?.status);

    const { cancelSummarizeResumeTask } = useSummarizeResumeApi();
    const [fileRecords, setFileRecords] = useState([]);
    const [currentUserAccess, setCurrentUserAccess] = useState(null);
    const [hasLoadedAccess, setHasLoadedAccess] = useState(false);
    const {
        showSuccess,
        showError
    } = useNotification();
    const isMounted = useMountedRef();

    const originalFiles = useMemo(() => {
        if (!fileRecords || !Array.isArray(fileRecords)) return [];
        return fileRecords.filter(record =>
            record.version === 0 || record.version === 1
        );
    }, [fileRecords]);

    const loadUserAccess = useCallback(async () => {
        if (!isFinished || hasLoadedAccess || !inputIdentifier || !hasAccess) return;

        try {
            const accessData = await get(`/api/TaskFileAccess/GetUserTaskAccess/${encodeURIComponent(inputIdentifier)}`);
            setCurrentUserAccess(accessData);
        } catch (error) {
            console.error('Error loading user access:', error);
            showError('Ошибка загрузки данных доступа');
        } finally {
            setHasLoadedAccess(true);
        }
    }, [isFinished, hasLoadedAccess, inputIdentifier, hasAccess, showError]);

    const handleCreatorTransferred = useCallback(async () => {
        setHasLoadedAccess(false);
        await loadUserAccess();
    }, [loadUserAccess]);

    useEffect(() => {
        if (isFinished && status?.result && hasAccess) {
            let records = [];
            if (Array.isArray(status.result)) {
                records = status.result;
            } else if (status.result?.resultAsTable) {
                records = status.result.resultAsTable;
            }
            if (records.length > 0) {
                setFileRecords(records);
                loadUserAccess();
            }
        }
    }, [isFinished, status, hasAccess, loadUserAccess]);

    useEffect(() => {
        const errors = [accessError, idError, fileNamesError, commentError, statusError]
            .filter(error => error);

        errors.forEach(error => showError(error));
    }, [accessError, idError, fileNamesError, commentError, statusError, showError]);

    const handleFileUpdated = useCallback(async (updateType = null) => {
        try {
            await fetchStatus();

            if (updateType === 'newVersion') {
                showSuccess('Новая версия файла успешно сохранена');
                setHasLoadedAccess(false);
                await loadUserAccess();
            } else if (updateType === 'delete') {
                showSuccess('Версия успешно удалена');
                setHasLoadedAccess(false);
                await loadUserAccess();
            }
        } catch (error) {
            console.error('Failed to refresh file data:', error);
            showError('Ошибка при обновлении данных файла');
        }
    }, [fetchStatus, loadUserAccess, showError, showSuccess]);

    const handleCancel = useCallback(async () => {
        try {
            await cancelSummarizeResumeTask(inputIdentifier);
            if (isMounted.current) {
                window.location.href = AI_UTILITIES.SUMMARIZE_RESUME.path;
            }
        } catch (err) {
            if (isMounted.current) {
                showError(err.message);
            }
        }
    }, [inputIdentifier, cancelSummarizeResumeTask, showError]);

    const shouldLoadData = isLoadingFileNames || isLoadingId || isLoadingComment ||
        (isFinished && fileRecords.length > 0 && !hasLoadedAccess);

    if (isCheckingAccess || (shouldLoadData && !hasLoadedAccess)) {
        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <CircularProgress />
                <p>Проверка доступа...</p>
            </div>
        );
    }

    if (hasAccess === false || (shouldLoadData && !currentUserAccess)) {
        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <p>Доступ запрещен</p>
            </div>
        );
    }

    if (hasAccess && shouldLoadData) {
        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <CircularProgress />
                <p>Загрузка данных...</p>
            </div>
        );
    }

    if (!status?.canCancel) {
        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <CircularProgress />
                <p>Ожидание запуска...</p>
            </div>
        );
    }

    return (
        <div>
            {hasAccess && fileNames && id && (
                <ProcessingStatus
                    id={id}
                    status={status}
                    fileNames={fileNames}
                    comment={comment}
                    error={status?.error || statusError}
                    isActive={isProcessing}
                    isWaiting={isWaiting}
                    currentUserAccess={currentUserAccess}
                    fileRecords={originalFiles}
                    onCreatorTransferred={handleCreatorTransferred}
                    timerElapsedTime={
                        isProcessing
                            ? timerElapsedTime
                            : (status?.durationSeconds ?? timerElapsedTime)
                    }
                />
            )}

            {hasAccess && isFinished && fileRecords.length > 0 && hasLoadedAccess && (
                <FileTable
                    id={id}
                    records={fileRecords}
                    inputIdentifier={inputIdentifier}
                    onFileUpdated={handleFileUpdated}
                    currentUserAccess={currentUserAccess}
                />
            )}

            {hasAccess && (
                <ActionButtons
                    onCancel={isProcessing ? handleCancel : null}
                    cancelText="Отменить резюмирование"
                    inputIdentifier={inputIdentifier}
                    currentUserAccess={currentUserAccess}
                />
            )}
        </div>
    );
};